// ─── The dashboard date filter's codec ────────────────────────────────────────
// A dashboard date filter is stored (and sent to Looker) as a Looker DATE FILTER
// EXPRESSION — "today", "last 30 days", "2026-01-01 to 2026-02-01". That string
// is the whole contract: the value travels untouched through the filter map into
// the tile query (server/query.js), so the picker's only job is to read and
// write it correctly.
//
// PURE module (no React, no fetch) so the expression rules are unit-testable —
// see test/dateFilter.test.js, which round-trips every shape.
//
// THE OFF-BY-ONE THAT MATTERS: in Looker, "A to B" runs from A up to but NOT
// INCLUDING B, and "before B" likewise stops short of B. A human picking
// 1–7 May means the 7th INCLUDED, so an end date chosen in the picker is written
// out as the day AFTER it, and read back as the day before the boundary. Without
// that, "range ends today" quietly dropped today's sales — the exact class of
// bug this picker exists to remove.

const DAY = /^\d{4}-\d{2}-\d{2}$/;

// LOOKER WRITES SLASHES. Its own date filter expressions are "2026/09/01 to
// 2026/09/23", and an imported dashboard arrives carrying them — reading only
// dashes meant every real value fell through as an unrecognised expression and
// the user got a text box to type in. Values are normalised on the way IN (the
// raw string is still kept, so a hand-written expression is never rewritten);
// what the picker writes OUT stays ISO.
const toIsoDays = (s) => String(s).replace(/(\d{4})\/(\d{1,2})\/(\d{1,2})/g, (_, y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
// A midnight timestamp on either end of a range is the same calendar day.
const stripMidnight = (s) => s.replace(/(\d{4}-\d{2}-\d{2})[T ]00:00(:00)?(\.\d+)?Z?/g, '$1');

// Day arithmetic on the ISO string itself, in UTC — a date filter is a calendar
// date, never a moment, so local timezones must not shift it.
export function shiftDay(iso, days) {
  if (!DAY.test(String(iso || ''))) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const isDay = (v) => DAY.test(String(v || ''));

// Today in the browser's own day (what a person means by "today").
export function todayIso(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

// The quick presets. Each is a RELATIVE Looker expression on purpose: saved as a
// view (or as a client default) it keeps meaning "the last 30 days" tomorrow,
// instead of freezing the dates it was saved on.
//
// "Last 7 days" INCLUDES TODAY ("6 days ago for 7 days"). Looker's own
// "7 days ago for 7 days" ends yesterday, which on a sales dashboard silently
// hides the day you are actually asking about.
export const PRESETS = [
  { key: 'today', label: 'Today', expr: 'today' },
  { key: 'yesterday', label: 'Yesterday', expr: 'yesterday' },
  { key: 'last7', label: 'Last 7 days', expr: '6 days ago for 7 days', hint: 'today and the 6 days before it' },
  { key: 'last30', label: 'Last 30 days', expr: '29 days ago for 30 days', hint: 'today and the 29 days before it' },
  { key: 'thismonth', label: 'This month', expr: 'this month' },
  { key: 'lastmonth', label: 'Last month', expr: 'last month' },
  { key: 'thisyear', label: 'This year', expr: 'this year' },
];
const PRESET_BY_EXPR = new Map(PRESETS.map((p) => [p.expr, p]));
const PRESET_BY_KEY = new Map(PRESETS.map((p) => [p.key, p]));
export const presetExpr = (key) => (PRESET_BY_KEY.get(key) || {}).expr || '';

// ── Reading a value ──────────────────────────────────────────────────────────
// → { mode, preset, start, end, raw }
//   'all'    no value at all (every date)
//   'preset' one of the quick presets, verbatim
//   'range'  absolute dates: start and/or end (both INCLUSIVE, as picked)
//   'expr'   any other Looker expression — kept as typed, editable as text
export function parseDateValue(value) {
  const raw = String(value == null ? '' : value).trim();
  const base = { mode: 'all', preset: '', start: '', end: '', raw };
  if (!raw) return base;

  const preset = PRESET_BY_EXPR.get(raw.toLowerCase());
  if (preset) return { ...base, mode: 'preset', preset: preset.key };

  // Everything below reads the NORMALISED form (slashes → dashes, midnight
  // timestamps dropped) so a Looker-written value is understood as the range
  // it is, not shown as text to retype.
  const s = stripMidnight(toIsoDays(raw)).trim();

  const range = s.match(/^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/i);
  if (range) return { ...base, mode: 'range', start: range[1], end: shiftDay(range[2], -1) };

  const after = s.match(/^(?:after|on or after|from)\s+(\d{4}-\d{2}-\d{2})$/i);
  if (after) return { ...base, mode: 'range', start: after[1] };

  const before = s.match(/^before\s+(\d{4}-\d{2}-\d{2})$/i);
  if (before) return { ...base, mode: 'range', end: shiftDay(before[1], -1) };

  // "2026-09-01 for 7 days" — Looker's absolute-start, fixed-length window.
  const forDays = s.match(/^(\d{4}-\d{2}-\d{2})\s+for\s+(\d+)\s+days?$/i);
  if (forDays) return { ...base, mode: 'range', start: forDays[1], end: shiftDay(forDays[1], Math.max(1, Number(forDays[2])) - 1) };

  const oneDay = s.match(/^(?:on\s+)?(\d{4}-\d{2}-\d{2})$/i);
  if (oneDay) return { ...base, mode: 'range', start: oneDay[1], end: oneDay[1] };

  // A bare month ("2026-09") or year ("2026") is the whole of it.
  const month = s.match(/^(\d{4})-(\d{2})$/);
  if (month) return { ...base, mode: 'range', start: `${month[1]}-${month[2]}-01`, end: lastDayOfMonth(`${month[1]}-${month[2]}`) };
  const year = s.match(/^(\d{4})$/);
  if (year && Number(year[1]) >= 1970 && Number(year[1]) <= 2999) return { ...base, mode: 'range', start: `${year[1]}-01-01`, end: `${year[1]}-12-31` };

  return { ...base, mode: 'expr' };
}

// ── Writing a value ──────────────────────────────────────────────────────────
// Start and end are what the human picked, both inclusive. Either may be blank
// (an open end), and both blank means no constraint at all.
export function rangeToExpr(start, end) {
  const s = isDay(start) ? start : '';
  const e = isDay(end) ? end : '';
  if (s && e) {
    if (s === e) return s;                       // a single day is its own expression
    if (e < s) return `${e} to ${shiftDay(s, 1)}`; // picked back-to-front — read it the way they meant
    return `${s} to ${shiftDay(e, 1)}`;          // +1 day: Looker's "to" excludes its end
  }
  if (s) return `after ${s}`;                    // "after" INCLUDES its own day
  if (e) return `before ${shiftDay(e, 1)}`;      // "before" excludes its own day
  return '';
}

// ── The calendar grid ────────────────────────────────────────────────────────
// Kept here, pure, so the month maths the calendar draws with is unit-tested
// rather than living inside a React component (see test/dateFilter.test.js).
// A "month" is the ISO prefix "YYYY-MM".
export const monthOf = (iso) => String(iso || '').slice(0, 7);

export function addMonths(month, n) {
  const m = String(month || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

export function lastDayOfMonth(month) {
  const m = String(month || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return '';
  return new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).toISOString().slice(0, 10);
}

// The cells of a month, WEEK STARTING MONDAY (how a South African calendar
// reads), padded with '' so the grid's 7 columns line up.
export function monthGrid(month) {
  const first = `${month}-01`;
  if (!isDay(first)) return [];
  const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7; // Sun=0 → Mon=0
  const days = Number(lastDayOfMonth(month).slice(8));
  const cells = new Array(lead).fill('');
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7) cells.push('');
  return cells;
}

// ── What the value says ──────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const monthName = (month) => {
  const m = String(month || '').match(/^(\d{4})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
};
export function formatDay(iso) {
  if (!isDay(iso)) return String(iso || '');
  const [y, m, d] = iso.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

export function describeDateValue(value) {
  const p = parseDateValue(value);
  if (p.mode === 'all') return 'All dates';
  if (p.mode === 'preset') return (PRESET_BY_KEY.get(p.preset) || {}).label || p.raw;
  if (p.mode === 'range') {
    if (p.start && p.end) return p.start === p.end ? formatDay(p.start) : `${formatDay(p.start)} → ${formatDay(p.end)}`;
    if (p.start) return `From ${formatDay(p.start)}`;
    return `Up to ${formatDay(p.end)}`;
  }
  return p.raw;
}

// ── Is this filter a DATE filter? ────────────────────────────────────────────
// Looker tells us outright when the dashboard declares a date filter type. It
// often doesn't: an imported filter can arrive as a plain field filter on a date
// dimension, which is how the reported bug looked — the control fell through to
// the value dropdown and offered a list of individual dates (2014, 2015 — the
// first page of them, ascending), so a recent range was unreachable.
//
// So a date dimension is recognised by its own name too: Looker timeframe
// dimensions end in _date / _time / _raw (and warehouse columns in _at).
// Deliberately NOT matched: "days before event" / "days from launch", which are
// NUMBERS on a day axis — they keep the numeric-range control they have today.
const DATE_UI = new Set(['relative_timeframes', 'date_range_picker', 'day_range_picker']);
const DATE_FIELD = /(^|[._])[a-z0-9_]*(date|datetime|timestamp|time|raw)$/i;
const DATE_AT_FIELD = /_at$/i;
const DATE_TITLE = /\b(date|date range|period|timeframe)\b/i;
const NOT_A_DATE = /\bdays?\b|days_|_days/i;         // "Days Before Event" is a count, not a date
const NUMERIC_VALUE = /^\s*(?:[[(]\s*-?\d|[<>]=?\s*-?\d|-?\d+(?:\.\d+)?\s*$)/; // "[26,360]", ">=42", "7"

export function isDateFilter(filter, value) {
  const f = filter || {};
  const uiType = f.ui_config?.type;
  if (DATE_UI.has(uiType)) return true;
  if (f.type === 'date_filter') return true;
  // Anything already holding a number or a numeric range is not a date control,
  // whatever it is called — never hijack a working filter.
  const current = String(value == null ? (f.default_value || '') : value);
  if (NUMERIC_VALUE.test(current)) return false;
  const field = String(f.field || f.dimension || '');
  const title = String(f.title || f.name || '');
  if (NOT_A_DATE.test(field) || NOT_A_DATE.test(title)) return false;
  if (DATE_FIELD.test(field) || DATE_AT_FIELD.test(field)) return true;
  if (DATE_TITLE.test(title) && !!field) return true;
  // Last resort: the value ITSELF is unmistakably a date range ("2026/09/01 to
  // 2026/09/23"). Whatever the field is called, a filter already holding a date
  // range must offer a date range picker — never a list of days to tick.
  const p = parseDateValue(current);
  return (p.mode === 'range' && (isDay(p.start) || isDay(p.end))) && !!field;
}

// ── The safety net ───────────────────────────────────────────────────────────
// A filter we did NOT recognise still falls through to the value dropdown. If
// what Looker sends back is a list of days, that dropdown is the broken control
// this ticket is about — so the filter bar offers the picker instead of making
// anyone tick dates one by one. Suggestions are strings or { value, label }.
const DATEISH = /^\d{4}[-/]\d{1,2}(?:[-/]\d{1,2})?(?:[T ]|$)/;
export function looksLikeDates(suggestions) {
  const vals = (suggestions || [])
    .map((s) => String(typeof s === 'string' ? s : (s && s.value) || ''))
    .filter(Boolean);
  if (vals.length < 5) return false;               // too few to be sure; leave it alone
  const dates = vals.filter((v) => DATEISH.test(v)).length;
  return dates >= Math.ceil(vals.length * 0.9);
}
